import React, { useState, useEffect, useReducer, useRef } from 'react';
import { Route, HashRouter as Router, Switch } from 'react-router-dom';
import { injectIntl } from 'react-intl';
import { v4 as uuidv4 } from 'uuid';
import { getLayout, i18nIntl, isNotEmpty } from '../../lib/ep.js';
import { crosswords, getHints, getTwinLetters } from '../../ts/games/crosswords.ts';
import Diacritics from '../../lib/diacritic.js';
import { useThrottle } from '../../lib/hooks.js';
import { useAccount } from '../context/accountContext.js';

import ActionButton, { SecondaryButton } from '../builder/ActionButton';
import ActionLink from '../builder/ActionLink.js';
import BuilderCanvas from '../builder/BuilderCanvas';
import BuilderMain from '../builder/BuilderMain.js';
import BuilderSidebar from '../builder/BuilderSidebar';
import ButtonCollection from '../builder/ButtonCollection.js';
import Checkbox from '../builder/Checkbox.js';
import ColorPicker from '../builder/ColorPicker.js';
import DynamicFields, { createField } from '../builder/DynamicFields.js';
import FloatingLayer from '../builder/FloatingLayer.js';
import Input from '../builder/Input';
import Message from '../builder/Message.js';
import { NavigationTabsBuilder } from '../builder/NavigationTabs';
import Model from '../builder/Model';
import Range from '../builder/Range.js';
import SectionTitle from '../builder/SectionTitle.js';
import TextArea from '../builder/TextArea.js';
import Tooltip from '../builder/Tooltip.js';

import AICrosswordsDefinitionGenerator from './AICrosswordsDefinitionGenerator.js';
import BuilderSubSectionTitle from './BuilderSubSectionTitle.js';
import MultiOptionsCreator, { PlanAlertIA } from './MultiOptionsCreator.js';

import { GameContainerWrapper } from '../front/GameContainer.js';
import Layout from '../front/Layout';
import CrosswordsClient, { DownloadImagesLayer } from '../front/CrosswordsClient.js';
import LeveledHTMLText from '../front/helpers/LeveledHTMLText.js';

import BulkFromFile from '../utils/BulkFromFile.js';
import StageFinalMessages from '../games/StageFinalMessages.js';
import GameInstructionsBuilder from './GameInstructionsBuilder.js';
import GameScoreBuilder from './GameScoreBuilder.js';

const CrosswordField = ({ model, change, ...props }) => {
	const i18n = (m, c) => i18nIntl(m, props.intl, c);

	const in_layer = props?.in_layer ?? false;
	const is_locked = props?.is_locked ?? false;

	const delayed_change = useThrottle(change, 500);
	const [ ai_definition, setAiDefinition ] = useState(false);

	const acceptable_word_for_ia = ((model?.word ?? '').length >= 3) ? true : false

	const { MyAccount } = useAccount();
	//const has_min_plan = (MyAccount.hasMinPlan(4)) ? true : false;
	const has_min_plan = true;//09/02/2024 => Es permet l'accès a AI a tothom 

	return (
		<>
			{(is_locked) && (
				<div class="gui_not_in_top">{i18n("word", 'ucfirst')}: <strong>{model.word}</strong></div>
			)}

			<Model
				model={model}
				change={delayed_change}
				errors={props.errors != undefined && props.errors[model.id]}
			>
				{(!is_locked) && (
					<Input
						name='word'
						max_length={15}
						exclude_from_count={[' ', '-']}
						placeholder="Word"
						_class={(in_layer) ? { first:true } : null}
						autotranslate={false}
					>{i18n("word", 'ucfirst')}</Input>
				)}

				<TextArea
					name='definition'
					label_props={(acceptable_word_for_ia) ? {
						_extra_component:
							<ActionLink
								onClick={() => setAiDefinition(true)}
							>Generate with AI</ActionLink>
					} : null}
				>Definition</TextArea>
			</Model>

			{(ai_definition && !has_min_plan) && (
				<PlanAlertIA
					type_selector={props.type_selector_data} 
					close={() => setAiDefinition(false)}
					intl={props.intl}
				/>
			)}

			{(ai_definition && has_min_plan) && (
				<AICrosswordsDefinitionGenerator
					promotion = {props.promotion}
					model={model}
					close={() => setAiDefinition(false)}
					onSuccess={(definition) => {
						change('definition', definition);
						setAiDefinition(false);
					}}
				/>
			)}
		</>
	)
};

const generateCrossword = (stage,key,num_hints) => {
	const unique = {};
	const words = (stage?.crosswords_words ?? []).map(word => {
		if(unique[word.word] == undefined) unique[word.word] = 0;
		unique[word.word]++;
		return {
		...word,
		x:parseInt(word.x),
		y:parseInt(word.y),
	}});

	let non_empty = words.filter((w) => (isNotEmpty(w, 'word')));
	if(words.length >0 && unique[non_empty[non_empty.length-1].word] == 2) non_empty = non_empty.slice(0,-1);

	let crosswords_positions = stage?.crosswords_positions ?? [];

	if(!stage.crosswords_locked && (stage.crossword_key != undefined && stage.crossword_key != key)) {
		crosswords_positions = crosswords(non_empty, 17,crosswords_positions);

		if(!Array.isArray(crosswords_positions)) {
			if(crosswords_positions.status == 0 && crosswords_positions.reason == 'isolated') {
				return {error:'isolated',data:crosswords_positions.isolated};
			}

			if(crosswords_positions.status == 0 && crosswords_positions.time_limit) {
				const offender = non_empty.slice(-1);
				return {error:'isolated',data:[offender[0].word]};
			}
	
			if(crosswords_positions.status == 0 ) {
				return {error:'isolated',data:crosswords_positions.missing};
			}
		}
	}

	crosswords_positions = crosswords_positions.map((word) => ({
		...word,
		x:parseInt(word.x),
		y:parseInt(word.y),
	}));
	
	stage.crosswords_positions = crosswords_positions;
	if(!stage.crosswords_hints || (stage.crossword_old_hints != undefined && stage.crossword_old_hints != num_hints)) {
		const twin_letters = getTwinLetters(crosswords_positions, 17);
		stage.crosswords_hints = getHints(crosswords_positions, twin_letters, (stage?.crosswords_num_hints ?? 3));
	}
	stage.crossword_key = key;
	stage.crossword_old_hints = num_hints;

	return stage;
}

const GameSettings = ({ stage, change, ...props }) => {
	const [ errors, setErrors ] = useState({});

	const min_time = 5;		// in minutes
	const max_time = 45;	// in minutes

	const min_rows = 15;
	const min_cols = 5;
	const default_cols = 6;
	const stack_num_rows = Math.max(parseInt(stage?.stack_num_rows ?? 30), min_rows);

	const layout_props = getLayout(props.promotion);
	const words = stage?.crosswords_words ?? [];
	const crossword_key = `${words.reduce((p, c) => (p + c.word), "")}`;
	const stage_for_canvas = generateCrossword(stage, crossword_key, stage.crosswords_num_hints);

	const convertTimeToMinutes = (time) => {
		return parseInt(time / 60);
	};

	const convertTimeToSeconds = (time) => {
		return parseInt(time * 60);
	};

	const changeSidebar = (k, v) => {
		if(k == 'game_time') {
			change('game_time', convertTimeToSeconds(v));
			return;
		}
		change(k, v);
	};

	const max_cols = stack_num_rows - 4;
	const stack_initial_row = Math.min(Math.max(parseInt(stage?.stack_initial_row ?? default_cols), min_cols), max_cols);

	let num_words = (stage?.crosswords_words ?? []).length;
	if(num_words < 1) num_words = 10;

	return (
		<>
			<BuilderSidebar onSave={props.saveStage} errors={errors}>
				<NavigationTabsBuilder
					relative_selection={true}
					active={`${props.nav_active}`}
					sections={props?.nav_sections ?? []}
				>{props.stage_indicator}</NavigationTabsBuilder>

				<article>
					<Model model={stage} change={changeSidebar}>
						<Range
							min={min_time}
							max={max_time}
							step={5}
							label_props={{
								addition:"in min.",
							}}
							_class={{ size:50, first:true }}
							name="game_time"
							value={convertTimeToMinutes(stage?.game_time ?? ((max_time - min_time) / 2))}
						>Time of game</Range>

						<Input
							name='crosswords_points'
							placeholder='100'
							_type='number'
							_class={{ size:20 }}
							label_props={{
								tooltip:<Tooltip autotranslate={false}>
									{props.intl.formatMessage({ id:"The user earns {points_per_word} points for each correct word. You can give a bonus for completing the entire crossword. The default is {bonus_points} points." }, { points_per_word:10, bonus_points:100 })}
								</Tooltip>
							}}
						>Extra points to solve the crosswords</Input>
					</Model>
				</article>

				<article class="gui_not_in_top_huge">
					<BuilderSubSectionTitle
						title="Help"
						icon='knk-tip'
					/>

					<Model model={stage} change={changeSidebar}>
						<Checkbox
							name="crosswords_enable_solve_words"
							value={stage?.crosswords_enable_solve_words ?? false}
						>Allow users to reveal words</Checkbox>

						{(stage?.crosswords_enable_solve_words ?? false) && (
							<Range
								min={1}
								max={num_words}
								step={1}
								_class={{ size:50 }}
								name="crosswords_num_solves"
								value={Math.min(num_words, ((stage.crosswords_num_solves != undefined) ? parseInt(stage.crosswords_num_solves) : 3))}
							>How many do you allow?</Range>
						)}

						<Checkbox
							name="crosswords_display_validations"
							value={stage?.crosswords_display_validations ?? false}
						>Display correct / incorrect letters</Checkbox>
					</Model>
				</article>

				<article class="gui_not_in_top_huge">
					<BuilderSubSectionTitle
						title="Board colors"
						icon='knk-artist-mode'
					/>

					<Model model={stage} change={changeSidebar}>
						<ColorPicker
							name='bg_color'
							color={stage?.bg_color ?? 'rgba(0, 0, 0, 1)'}
							_class={{ size:50 }}
						>Background color</ColorPicker>

						<ColorPicker
							name='crosswords_cell_color'
							color={stage?.crosswords_cell_color ?? 'rgba(255, 255, 255, 1)'}
							_class={{ size:50 }}
						>Letters background color</ColorPicker>

						<ColorPicker
							name='text_color'
							color={stage?.text_color ?? 'rgba(0, 0, 0, 1)'}
							_class={{ size:50 }}
						>Letters color</ColorPicker>
					</Model>
				</article>

				<article class="gui_not_in_top_huge">
					<BuilderSubSectionTitle
						title="Selected word colors"
						icon='knk-artist-mode'
					/>

					<Model model={stage} change={changeSidebar}>
						<ColorPicker
							name='highlight_color'
							color={stage?.highlight_color ?? 'rgba(255, 255, 0, 1)'}
							_class={{ size:50 }}
						>Letters background color</ColorPicker>

						<ColorPicker
							name='highlight_text_color'
							color={stage?.highlight_text_color ?? 'rgba(0, 0, 0, 1)'}
							_class={{ size:50 }}
						>Letters color</ColorPicker>
					</Model>
				</article>

				{(stage?.crosswords_display_validations ?? false) && (
					<article class="gui_not_in_top_huge">
						<BuilderSubSectionTitle
							title="Correct letter color"
							icon='knk-artist-mode'
						/>

						<Model model={stage} change={changeSidebar}>
							<ColorPicker
								name='crosswords_ok_bg_color'
								color={stage?.crosswords_ok_bg_color ?? layout_props.other.ribbon_open_background_color}
								_class={{ size:50 }}
							>Letters background color</ColorPicker>

							<ColorPicker
								name='crosswords_ok_text_color'
								color={stage?.crosswords_ok_text_color ?? layout_props.other.ribbon_open_text_color}
								_class={{ size:50 }}
							>Letters color</ColorPicker>
						</Model>
					</article>
				)}
			</BuilderSidebar>

			<BuilderCanvas fit_to_window="content">
				<Layout
					promo={props.promotion}
					is_builder={true}
					page="quiz"
					menus={props.promoModel.Custom__Menus}
					layout_extra_classes="is_new_game"
				>
					<div style={{width:'100%'}}>
						{(Object.keys(errors).length == 0) ? (
							<CrosswordsClient
								key={`${stack_initial_row}_${stack_num_rows}`}
								stage={{ Stage:stage_for_canvas }}
								promotion={{ Promotion:props.promotion }}
								is_builder={true}
								is_playable={true}
								section="settings"
							/>
						) : (
							<GameContainerWrapper>
								<div class="is_fullscreen relative_to_container empty_game_warning not_awesome orangeBg knk-arrow-l-big">
									{Object.values(errors).map((error, i) => {
										let is_object = (typeof error === 'object') ? true : false;
										if(is_object) return null;
										return (
											<p key={`error${i}`}>{error}</p>
										)
									})}
								</div>
							</GameContainerWrapper>
						)}
					</div>
				</Layout>
			</BuilderCanvas>
		</>
	)
};

const GameWords = ({ stage, change, ...props }) => {
	const i18n = (m, c) => i18nIntl(m, props.intl, c);

	const [ errors, setErrors ] = useState({});
	const [ word_errors, setWordErrors ] = useState({});
	const [ words_with_errors, setWordsWithErrors ] = useState([]);
	const [ model, setModel ] = useState({});
	const [ show_solved, setShowSolved ] = useState(true);
	const [ create_word, setCreateWord ] = useState(false);
	
	const [ stage_for_canvas, setStageForCanvas] = useState({})
	
	const words = stage?.crosswords_words ?? [];
	const crossword_key = `${words.reduce((p, c) => (p + c.word), "")}`;
	const full_key = `${crossword_key}_${stage.crosswords_num_hints}`;
	const promotion = props?.promotion ?? false;

	const undo = useRef({words:stage?.crosswords_words != undefined ? stage.crosswords_words.map(w=>({...w})) : [],positions:null});

	const [ state, dispatch ] = useReducer((state, action) => {
		switch(action.type) {
			case 'SET_STATE':
				return { ...state,current_state:action.state };

			default:
				return state;
		}
	}, {
		current_state:'GENERATED',
	});

	useEffect(() => {
		if(state.current_state !== 'GENERATING') return;
		undo.current.positions = stage.crosswords_positions;
		
		const positions = generateCrossword(stage, crossword_key, stage.crosswords_num_hints);

		setErrors({});

		if((positions?.error ?? 'word') == 'isolated') {
			const _errors = { isolated:i18n("Some words could not be placed.") };

			const _word_errors = {};
			const _words_with_errors = [];
			positions.data.map((_w) => {
				_words_with_errors.push({
					title:_w.toUpperCase(),
					color:'--main-bg-color',
				});
			});

			words.map((_w) => {
				if(positions.data.includes(_w.word.toLowerCase())) _word_errors[_w.id] = i18n("Unable to place");
			});

			dispatch({ type:'SET_STATE', state:'ERROR' });

			setWordErrors(_word_errors);
			setErrors(_errors);
			setWordsWithErrors(_words_with_errors);
			return;
		} else {
			dispatch({ type:'SET_STATE', state:'GENERATED' });
		}

		validate();
 
		setStageForCanvas(positions);
	}, [ state.current_state ]);

	useEffect(() => {
		dispatch({type:'SET_STATE',state:'SHOULD_GENERATE'});
	}, [ crossword_key, stage.crosswords_num_hints ]);

	useEffect(() => {
		if(state.current_state != 'SHOULD_GENERATE') return;

		setTimeout(() => {
			dispatch({ type:'SET_STATE', state:'GENERATING' });
		}, 500);
	}, [ state.current_state ]);

	const is_locked = stage_for_canvas?.crosswords_locked ?? false;
	
	const addWord = () => {
		const current_words = stage?.crosswords_words ?? [];
		setCreateWord(false);
		undo.current.words = current_words;
		change('crosswords_words', [ ...current_words, model ]);
	};

	const addWords = (words) => {
		let _words = stage?.crosswords_words ?? [];
		undo.current.words = _words;
		change('crosswords_words', [
			..._words,
			...words.map((w) => ({
				id:uuidv4(),
				type:'word',
				...w,
				word:w.word.replace(/^\s+|\s+$/,""),
			}))
		]);

		setCreateWord(false);
	};

	const validate = (v) => {
		v = (v == undefined) ? stage.crosswords_words : v;
		let _word_errors = {};

		v.map((w) => {
			if(_word_errors[w.id] == undefined) _word_errors[w.id] = {};
			
			if(!isNotEmpty(w, 'word')) {
				_word_errors[w.id] = i18n("Mandatory word");
			}

			if(w.word != undefined && w.word.length > 0) {
				let already_word = v.find((word) => {
					return (word.id != w.id && word.word != undefined && (word.word).toLowerCase() == (w.word).toLowerCase()) ? true : false;
				});
				if(already_word) _word_errors[w.id] = i18n("Repeated");

				if((w.word).replace(/\s+/g, '').length > 15) _word_errors[w.id] = i18n("Too long");
			}

			const latin1Regex = /^[\u0000-\u00FF]*$/;
			if(!latin1Regex.test(w.word))_word_errors[w.id] = i18n("Invalid");
			
			if(Object.keys(_word_errors[w.id]).length == 0) delete _word_errors[w.id];
		});

		setWordErrors(_word_errors);
		change('crosswords_words', v);
		// setLoading(true);

		// setTimeout(() => {
		// 	setLoading(false);
		// }, 0);
	};	

	const changeSidebar = (k, v) => {
		change(k, v);
	};

	const lockGame = () => {
		change('crosswords_locked', true);
		props.saveStage();
	};

	const unlockGame = () => {
		change('crosswords_locked', false);
		props.saveStage();
	};

	const undoChanges = () => {
		setStageForCanvas(undo.current.positions);
		change('crosswords_words',undo.current.words);
	}

	const changeWords = (v) => {
		v = v.map((word) => {
			word.word = Diacritics.clean(word.word).toLowerCase();
			return word;
		});
		if (stage?.crosswords_words?.length > v.length) {
			undo.current.words = stage.crosswords_words;
		}

		changeSidebar('crosswords_words', v);
		if(stage?.crosswords_positions ?? false) {
			const patched = stage.crosswords_positions.map((word) => {
				const updated_definition = v.find((new_word) => new_word.id == word.id);
				if(!updated_definition) return word;

				word.definition = updated_definition?.definition;
				return word;
			});
			changeSidebar('crosswords_positions', patched);
		}
		if (v.length == 0) props.saveStage();
	};

	const type_selector_data = {
		title_ai:i18n("AI Word Generator"),
		single:i18n("definition",'ucfirst'),
		plural:i18n("definitions"),
		type:i18n("Crossword"),
	};
	
	const section_title = `${i18n("Words")} (${words.length})`;

	const dynamic_fields = [{
		type:'word',
		label:"Word",
		title:(f) => {
			if(isNotEmpty(f, 'word')) return f.word;
			return i18n("Word");
		},
	}];

	return (
		<>
			<BuilderSidebar onSave={props.saveStage} errors={errors}>
				<NavigationTabsBuilder
					relative_selection={true}
					active={`${props.nav_active}`}
					sections={props?.nav_sections ?? []}
				>{props.stage_indicator}</NavigationTabsBuilder>

				<article>
					<DynamicFields
						section_title={section_title}
						onChange={changeWords}
						field_types={dynamic_fields}
						ul_styles={{ maxHeight:'calc(100dvh - 395px)' }}
						sortable={false}
						can_copy={(!is_locked) ? true : false}
						can_delete={(!is_locked) ? true : false}
						can_create={(!is_locked) ? true : false}
						delete_all={true}
						fields={words}
						errors={word_errors}
						custom_add_button={(!is_locked) ? (
							<MultiOptionsCreator
								type_ai="crosswords"
								promotion={promotion}
								stage_id={stage.id}
								label_button="Add words"
								label_main="Add words"
								format_message="Only words with characters from the Latin alphabet are accepted: A, B, C, ..., Z"
								onCreateBulk={() => {
									setCreateWord('upload');
								}}
								onCreateSimple={() => {
									setModel(createField(i18n("Field"), 'word'));
									setCreateWord('simple');
								}}
								onSuccess={(w) => {
									addWords(w);
									props.saveStage();
								}}
							/>
						) : null}
					>
						<CrosswordField
							promotion_id={stage.promotion_id}
							promotion={promotion}
							errors={errors}
							is_locked={is_locked}
							intl={props.intl}
							type_selector_data={type_selector_data}
						/>
					</DynamicFields>

					{(words != undefined && words.length > 0) && (
						<>
							{(!is_locked) ? (
								<Model model={stage} change={changeSidebar}>
									<Range
										min={0}
										max={words?.length ?? 1}
										step={1}
										_class={{ size:40 }}
										name="crosswords_num_hints"
										value={(stage.crosswords_num_hints != undefined) ? parseInt(stage.crosswords_num_hints) : Math.min((words?.length ?? 1), 3)}
										label_props={{
											tooltip:<Tooltip>Number of letters you want to appear revealed at the beginning. Locks the game if you don't want them to regenerate each time.</Tooltip>
										}}
									>Number of revealed letters</Range>
								</Model>
							) : (
								<div class="gui_not_in_top">{i18n("Number of revealed letters")}: {stage?.crosswords_num_hints ?? Math.min((words?.length ?? 1), 3)}</div>
							)}
						</>
					)}
				</article>

				{(!is_locked) ? (
					<article class="gui_not_in_top_big">
						<p>{i18n("You can lock the crossword to prevent further changes and avoid regeneration. This is a recommended security measure once you have the final crossword.")}</p>

						<ButtonCollection _class={{ other:'gui_not_in_top_small' }}>
							<SecondaryButton
								onClick={() => lockGame()}
								//disabled={!enable_save_config}
							>Lock the crossword</SecondaryButton>
						</ButtonCollection>
					</article>
				) : (
					<article class="gui_not_in_top_big">
						<p>{i18n("The crossword is locked. You can only change word definitions. Unlock it if you want to add, remove or edit words.")}</p>

						<ButtonCollection _class={{ other:'gui_not_in_top_small' }}>
							<SecondaryButton
								onClick={unlockGame}
							>Unlock</SecondaryButton>

							<DownloadImagesLayer
								intl={props.intl}
								is_builder={true}
							/>
						</ButtonCollection>
					</article>
				)}

				{(create_word == 'simple') && (
					<FloatingLayer
						_class={{ size:'small' }}
						backButton={() => setCreateWord(false)}
					>
						<div>
							<SectionTitle>Add word</SectionTitle>

							<CrosswordField
								model={model}
								change={(k, v) => setModel((state) => {
									return {
										...state,
										[k]:v,
									}
								})}
								promotion_id={stage.promotion_id}
								promotion={props.promotion}
								errors={errors}
								in_layer={true}
								intl={props.intl}
								
							/>

							<ButtonCollection in_floating={true}>
								<ActionButton
									onClick={addWord}
								>Add</ActionButton>

								<SecondaryButton
									onClick={() => setCreateWord(false)}
								>Cancel</SecondaryButton>
							</ButtonCollection>
						</div>
					</FloatingLayer>
				)}

				{(create_word == 'upload') && (
					<FloatingLayer
						_class={{ size:'small' }}
						backButton={() => setCreateWord(false)}
						>
						<BulkFromFile
							title="Import your words/definitions"
							subtitle="You can import up to {max} words/definitions in bulk."
							max={30}
							promotion_id={promotion.id}
							type="crosswords"
							icon="knk-crosswords"
							type_import='upload'
							onSuccess={(w) => addWords(w)}
							close={()=>setCreateWord(false)}
							fields={[
								{ name:'word', icon:'knk-crosswords', label:i18n("Word"), position:1 },
								{ name:'definition', icon:'knk-search', label:i18n("Definition"), position:2 },
							]}
							instructions={<><p>{i18n("Create a CSV or text file with two columns, separated by a comma or semicolon. The first column corresponds to the word, and the second column the definition of this word.")}</p></>}
							extra_msg={<Message _class={{ other:'in_bottom'}}>
								<p>{i18n("If there are many repeated words, specify the topic more and generate new ones")}.</p>
							</Message>}
						/>
					</FloatingLayer>
				)}
			</BuilderSidebar>

			<BuilderCanvas
				fit_to_window="content"
				off_content={<>
					<Message
						_class={{ other:'in_top in_bottom' }}
						autotranslate={false}
						custom_title_content={true}
						title={<>{props.intl.formatMessage({ id:"Crossword view: {type}" }, { type:i18n((show_solved) ? "Solved" : "Unsolved") })}<ActionLink onClick={() => setShowSolved(!show_solved)} autotranslate={false}>{props.intl.formatMessage({ id:"Change view to: {type}" }, { type:i18n((show_solved) ? "Unsolved" : "Solved") })}</ActionLink></>}
						icon={false}
					/>
				</>}
			>
				<Layout
					promo={props.promotion}
					is_builder={true}
					page="quiz"
					menus={props.promoModel.Custom__Menus}
					layout_extra_classes="is_new_game"
				>
					{(['SHOULD_GENERATE', 'GENERATING'].includes(state.current_state)) && (
						<div style={{width:'100%'}}>
							<GameContainerWrapper>
								<div class="is_generating_game is_fullscreen relative_to_container not_awesome empty_game_warning lightgrayBg knk-loading">
									<LeveledHTMLText>
										<h1>{i18n("Generating the crossword")}</h1>
										<p>
											{i18n("Please, be patient.")}<br />
											{props.intl.formatMessage({ id:"This process may take up to {duration} seconds." }, { duration:10 })}
										</p>
									</LeveledHTMLText>

									{/*<div
										class='gui_progress_bar'
									>
										<span
											class='bar'
											style={{ '--_animation-duration':'10s' }}
										/>
									</div>*/}
								</div>
							</GameContainerWrapper>
						</div>
					)}

					{(!['SHOULD_GENERATE', 'GENERATING'].includes(state.current_state)) && (
						<div style={{width:'100%'}}>
							{(Object.keys(errors).length == 0) && (
								<CrosswordsClient
									key={full_key}
									stage={{ Stage:stage_for_canvas }}
									promotion={{ Promotion:props.promotion }}
									is_builder={true}
									enable_export={true}
									is_playable={false}
									section="design"
									show_solved={show_solved}
								/>
							)}

							{(Object.keys(errors).length > 0 && words_with_errors.length == 0) && (
								<GameContainerWrapper>
									<div class="is_fullscreen relative_to_container not_awesome empty_game_warning orangeBg sided_icon knk-arrow-l-big">
										{Object.keys(errors).map((error, i) => {
											if(typeof errors[error] === 'object') return null;

											return (
												<p key={`error${i}`}>{errors[error]}</p>
											)
										})}
									</div>
								</GameContainerWrapper>
							)}

							{(Object.keys(errors).length > 0 && words_with_errors.length > 0) && (
								<GameContainerWrapper>
									<div class="is_fullscreen relative_to_container not_awesome empty_game_warning redBg knk-alert whiteColor">
										{Object.keys(errors).map((error, i) => {
											if(error != 'isolated') return null;

											return (
												<React.Fragment key={`error${i}`}>
													<p>{errors[error]}</p>

													{/*<RibbonCollection
														items={words_with_errors}
														_class={{ other:'gui_not_in_top_tiny only_uppercase' }}
													/>*/}
												</React.Fragment>
											)
										})}

										<ButtonCollection
											_class={{ other:'gui_not_in_top' }}
										>
											<ActionButton
												onClick={undoChanges}
												color='white'
												_class={{ other:'redColor' }}
											>Revert changes</ActionButton>
										</ButtonCollection>
									</div>
								</GameContainerWrapper>
							)}
						</div>
					)}
				</Layout>
			</BuilderCanvas>
		</>
	)
};

const GameTest = ({ stage, change, ...props }) => {
	const restart = () => {
		change('reset', (stage.reset != undefined) ? (stage.reset + 1) : 0);
	};

	return (
		<>
			<BuilderSidebar without_ctas={true}>
				<NavigationTabsBuilder
					relative_selection={true}
					active={`${props.nav_active}`}
					sections={props?.nav_sections ?? []}
				>{props.stage_indicator}</NavigationTabsBuilder>

				<ButtonCollection>
					<ActionButton
						icon='knk-reload'
						onClick={() => restart()}
					>Restart game</ActionButton>
				</ButtonCollection>
			</BuilderSidebar>

			<BuilderCanvas fit_to_window="content">
				<Layout promo={props.promotion} is_builder={true} page="quiz" menus={props.promoModel.Custom__Menus} layout_extra_classes="is_new_game">
					<div style={{width:'100%'}}>
						<CrosswordsClient
							stage={{ Stage:stage }}
							promotion={{ Promotion:props.promotion }}
							is_builder={true}
							is_test={true}
						/>
					</div>
				</Layout>
			</BuilderCanvas>
		</>
	)
};

const CrosswordsBuilder = ({ base_route, stage, ...props }) => {
	const basename = `/stages/${stage.id}/crosswords`;
	const nav_sections = [
		{ label:"Go back", link:`/stages/${stage.id}/options`, icon:'knk-go-prev' },
		{ label:"Words", link:`${basename}/words`, icon:'knk-text' },
		{ label:"Gameplay settings", link:`${basename}/settings`, icon:'knk-settings' },
		{ label:"Instructions", link:`${basename}/instructions`, icon:'knk-help' },
		{ label:"Score screen", link:`${basename}/gamescore`, icon:'knk-like' },
		{ label:"Test the game", link:`${basename}/test`, icon:'knk-ok' },
	];

	const common_props = {
		stage,
		is_multistage:props.is_multistage,
		isCorporate:props.isCorporate,
		change:props.change,
		changeMulti:props.changeMulti,
		intl:props.intl,
		promotion:props.promotion,
		promoModel:props.promoModel,
		saveStage:props.saveStage,
		stage_indicator:props.stage_indicator,
		nav_sections:nav_sections,
	};

	return (
		<BuilderMain pm={props.promoModel}>
			<Router basename={basename} hashType="noslash">
				<Switch>
					<Route exact={true} path="/words">
						<GameWords
							{...common_props}
							nav_active={`/words`}
						/>
					</Route>

					<Route exact={true} path="/settings">
						<GameSettings
							{...common_props}
							nav_active={`/settings`}
						/>
					</Route>

					<Route exact={true} path="/instructions">
						<GameInstructionsBuilder
							{...common_props}
							nav_active={`/instructions`}
						>
							<CrosswordsClient
								stage={{ Stage:stage }}
								promotion={{ Promotion:props.promotion }}
								is_builder={true}
								section="instructions"
							/>
						</GameInstructionsBuilder>
					</Route>

					<Route exact={true} path="/gamescore">
						<GameScoreBuilder
							{...common_props}
							nav_active={`/gamescore`}
							exclude_cases={['out_of_moves']}
						>
							<CrosswordsClient
								stage={{ Stage:stage }}
								promotion={props.promotion}
								is_builder={true}
								section="score"
							/>
						</GameScoreBuilder>
					</Route>

					<Route exact={true} path="/test">
						<GameTest
							{...common_props}
							nav_active={`/test`}
						/>
					</Route>
				</Switch>
			</Router>
		</BuilderMain>
	)
};

export default injectIntl(CrosswordsBuilder);